import { ActiveAccount, BaseMaturityType, BaseReportSetting, DefaultSendDeskPositionReportDepthLevelType, Portfolio, PortfolioAccount, PortfolioKeyLimit, PortfolioKeyLimitAlert, PortfolioLimit, ReportReceiver, ReportSetting, RiskDesk, RiskReportMetric } from "src/@type/risk-management/types";
import { PositionReportRow } from "src/@type/risk-management/position-report";
import { GlobalPositionData } from "src/@type/risk-management/global-position";
import { DeskOpenPosition, DeskPositionData, GroupDesk } from "src/@type/risk-management/desk-position";
import { MonitorDeskStatus } from "src/@type/risk-management/monitor";

import { DeskPositionDataDTO, PositionReportRowDTO, PortfolioKeyLimitAlertDTO, PortfolioKeyLimitDTO, PortfolioLimitDTO, RiskDeskDTO, GroupDeskDTO, PortfolioDTO, PortfolioAccountDTO, ActiveAccountDTO, ReportReceiverDTO, DeskOpenPositionDTO, MonitorDeskStatusDTO, GlobalPositionDataDTO, FocusReportDataDTO, FocusReportSummaryDTO, TotalVerticalRowDTO, CommodityClusterFocusReportRowDTO, BellsAndAlertFocusReportRowDTO, ReportSettingDTO, BaseReportSettingDTO, PositionByDataDTO, PositionByRowDTO, BaseMaturityTypeDTO, PositionBySubDataDTO, RiskReportMetricDTO, GreeksReportDataDTO, GreeksReportSummaryRowDTO, GreeksReportByByRowDTO } from "./DTO";
import { BellsAndAlertFocusReportRow, CommodityClusterFocusReportRow, FocusReportData, FocusReportSummary, TotalVerticalRow } from "src/@type/risk-management/focus-report";
import { PositionByData, PositionByRow, PositionBySubData } from "src/@type/risk-management/position-by";
import { GreeksReportByByRow, GreeksReportData, GreeksReportSummaryRow } from "src/@type/risk-management/greeks-report";
export const mapperActiveAccountDTOToModel = (a: ActiveAccountDTO): ActiveAccount => ({
	Account: a.a
});

export const mapperRiskDeskDTOToModel = (d: RiskDeskDTO): RiskDesk => ({
    id: d.i,
    name: d.n,
	Active: d.a,
	ExternalCode1: d.e,
	GroupDeskID: d.g,
	SendInAllDeskPositionReport: d.r,
    SendDeskPositionReportDepthLevel: d.l ?? DefaultSendDeskPositionReportDepthLevelType,
	ReportReceivers: d.R?.map(mapperReportReceiverDTOToModel)
});

export const mapperGroupDeskDTOToModel = (g: GroupDeskDTO): GroupDesk => ({
	id: g.i,
	name: g.n
});

export const mapperGlobalPositionDataDTOToModel = (d: GlobalPositionDataDTO): GlobalPositionData => ({
    reportSetting: mapperReportSettingDTOToModel(d.b),
    byDeskRows: d.r.map(mapperPositionReportRowDTOToModel),
    limits: d.l.map(mapperPortfolioLimitDTOToModel),
	byDeskMetrics: d.i.map(mapperRiskReportMetricDTOToModel),
	desksStatus: d.s.map(mapperMonitorDeskStatusDTOToModel),
    riskDesks: d.z.map(mapperRiskDeskDTOToModel)
});

export const mapperDeskPositionDataDTOToModel = (d: DeskPositionDataDTO): DeskPositionData => ({
	desk: mapperRiskDeskDTOToModel(d.d),
    byDeskRows: d.r.map(mapperPositionReportRowDTOToModel),
	positions: d.p.map(mapperDeskOpenPositionDTOToModel),
    limits: d.l.map(mapperPortfolioLimitDTOToModel),
	byDeskMetrics: d.i.map(mapperRiskReportMetricDTOToModel),
    byCommodityRows: d.r1.map(mapperPositionReportRowDTOToModel),
    byCommodityMetrics: d.i1.map(mapperRiskReportMetricDTOToModel),
	desksStatus: d.s.map(mapperMonitorDeskStatusDTOToModel)
});

export const mapperFocusReportDataDTOToModel = (d: FocusReportDataDTO): FocusReportData => ({
    reportSetting: mapperReportSettingDTOToModel(d.b),
    summary: mapperFocusReportSummaryDTOToModel(d.f),
    totalVerticalRows: d.r.map(mapperTotalVerticalRowDTOToModel),
    commodityClusterFocusReportRows: d.c.map(mapperCommodityClusterFocusReportRowDTOToModel),
    bellsAndAlersRows: d.d.map(mapperBellsAndAlersRowDTOToModel),
    desksStatus: d.s.map(mapperMonitorDeskStatusDTOToModel),
    portfolioLimits: d.l.map(mapperPortfolioLimitDTOToModel),
    riskDesks: d.z.map(mapperRiskDeskDTOToModel)
});

const mapperPositionBySubDataDTOToModel = (d: PositionBySubDataDTO): PositionBySubData => ({
    rows: d.r.map(mapperPositionByRowDTOToModel),
    metrics: d.m.map(mapperRiskReportMetricDTOToModel)
});

export const mapperPositionByDataDTOToModel = (d: PositionByDataDTO): PositionByData => ({
    reportSetting: mapperReportSettingDTOToModel(d.b),
    desksStatus: d.s.map(mapperMonitorDeskStatusDTOToModel),
    MT: mapperPositionBySubDataDTOToModel(d.m),
    Nominal: mapperPositionBySubDataDTOToModel(d.n),
    riskDesks: d.z.map(mapperRiskDeskDTOToModel)
});

const mapperPositionByRowDTOToModel = (d: PositionByRowDTO): PositionByRow => ({
    id: d.a,
    Level1ID: d.b,
    Level1Name: d.c,
    Level2ID: d.d,
    Level2Name: d.e,
    Level3ID: d.f,
    Level3Name: d.g,
    Level4ID: d.h,
    Level4Name: d.i,
    Total: d.j,
    TotalLessOne: d.k,
    TotalDODChange: d.l,
    TotalInUSD: d.m,
    TotalDayPnL: d.n,
    TotalVaR: d.o,
    TotalVaRES: d.p,
    ...mapperBaseMaturityTypeDTOToModel(d),
    HasTChanges: d.ht,
    TChangedKeys: d.tk,
    Status: d.st
});

const mapperPositionReportRowDTOToModel = (r: PositionReportRowDTO): PositionReportRow => ({
    id: r.a0,
	DeskId: r.a,
	Desk: r.b,
	PortfolioId: r.c,
	Portfolio: r.d,
	CommodityClusterId: r.e,
	CommodityCluster: r.f,
	CommodityId: r.g,
	Commodity: r.h,
	OptionTypeId: r.i,
	OptionType: r.j,
	Total: r.k,
	TotalLessOne: r.l,
	TotalDelta: r.m,
	TotalInUSD: r.n,
	TotalInUSDLessOne: r.o,
	TotalInUSDDelta: r.p,
	TotalDayPnL: r.q,
	TotalMtdPnL: r.r,
	TotalYtdPnL: r.s,
	TotalVaR: r.t,
	TotalVaRLessOne: r.u,
	TotalVaRDelta: r.v,
	TotalVaRES: r.w,
	TotalDrawDown: r.x,
    TotalMaxDrawDown: r.y,
	GreekDelta: r.z,
	GreekGamma: r.A,
	GreekVega: r.B,
	GreekTheta: r.C,
    HasTChanges: r.ht,
    TChangedKeys: r.tk,
    Status: r.st,
	...mapperBaseMaturityTypeDTOToModel(r)
});

const mapperBaseMaturityTypeDTOToModel = (r: BaseMaturityTypeDTO): BaseMaturityType => ({
    Maturity0: r.m0,
	Maturity1: r.m1,
	Maturity2: r.m2,
	Maturity3: r.m3,
	Maturity4: r.m4,
	Maturity5: r.m5,
	Maturity6: r.m6,
	Maturity7: r.m7,
	Maturity8: r.m8,
	Maturity9: r.m9,
	Maturity10: r.m10,
	Maturity11: r.m11,
	Maturity12: r.m12,
	Maturity13: r.m13,
	Maturity14: r.m14,
	Maturity15: r.m15,
	Maturity16: r.m16,
	Maturity17: r.m17,
	Maturity18: r.m18,
	Maturity19: r.m19,
	Maturity20: r.m20,
	Maturity21: r.m21,
	Maturity22: r.m22,
	Maturity23: r.m23,
	Forward: r.mp
})

const mapperDeskOpenPositionDTOToModel = (p: DeskOpenPositionDTO): DeskOpenPosition => ({
	date: p.a,
    DeskId: p.b,
    CodeId: p.c,
    ProductId: p.d,
    Product: p.e,
    Instrument: p.f,
    Strike: p.g,
    Expiry: p.h,
    Lots: p.i,
    Delta: p.j,
    NetLots: p.k
});

const mapperRiskReportMetricDTOToModel = (r: RiskReportMetricDTO): RiskReportMetric => ({
	DeskID: r.a,
    ObjectKey: r.b,
    ObjectID: r.c,
    MetricKey: r.d,
    MetricValue: r.e
});

const mapperPortfolioLimitDTOToModel = (l: PortfolioLimitDTO): PortfolioLimit => ({
    PortfolioId: l.a,
    Limits: l.b.map(mapperPortfolioKeyLimitDTOToModel)
});

const mapperPortfolioKeyLimitDTOToModel = (l: PortfolioKeyLimitDTO): PortfolioKeyLimit => ({
	key: l.a,
    value: l.b,
    alerts: l.c.map(mapperPortfolioKeyLimitAlertDTOToModel) 
});

const mapperFocusReportSummaryDTOToModel = (s: FocusReportSummaryDTO): FocusReportSummary => ({
    SumOfVaRs: s.a,
    DiversifiedVaR: s.b,	
    VaRDivEffect: s.c,
    SumOfAbsTotal: s.d,
    TotalPos: s.e,
    PosDivEffect: s.f,
    SumOfAbsTotalInUSD: s.g,
    TotalInUSD: s.h,
    TotalInUSDDivEffect: s.i
});

const mapperTotalVerticalRowDTOToModel = (r: TotalVerticalRowDTO): TotalVerticalRow => ({
    PortfolioID: r.a,
    Portfolio: r.b,
    Total: {
        val: r.c,
        lim: r.d,
        usage: r.e
    },
    TotalInUSD: {
        val: r.f,
        lim: r.g,
        usage: r.h
    },
    DayPnL: r.i,
    MTDPnL: r.j,
    YTDPnL: r.k,
    VaR: {
        val: r.l,
        lim: r.n,
        usage: r.o
    },
    VaRES: r.m,
    Drawdown: {
        val: r.p,
        lim: r.q,
        usage: r.r
    },
    StopLossUsage: r.s,
    Gamma: {
        val: r.t,
        lim: r.u,
        usage: r.v
    },
    Vega: {
        val: r.w,
        lim: r.x,
        usage: r.y
    },
    Theta: r.z,
    isBell: r.ib,
    isAlert: r.ia
});

const mapperCommodityClusterFocusReportRowDTOToModel = (c: CommodityClusterFocusReportRowDTO): CommodityClusterFocusReportRow => ({
    CommodityClusterID: c.a,
    CommodityCluster: c.b,
    Total: c.c,
    TotalInUSD: c.d,
    IndicDayPnL: c.e,
    VaR: c.f,
    VaRES: c.g
});

const mapperBellsAndAlersRowDTOToModel = (b: BellsAndAlertFocusReportRowDTO): BellsAndAlertFocusReportRow => ({
    ...mapperTotalVerticalRowDTOToModel(b),
    status: b.st,
    note: b.no
});

export const mapperPortfolioKeyLimitAlertDTOToModel = (a: PortfolioKeyLimitAlertDTO): PortfolioKeyLimitAlert => ({
	id: a.i,
	key: a.k,
	per: a.p,
    flagColor: a.c,
    bellNote: a.n
});

export const mapperPortfolioDTOToModel = (p: PortfolioDTO): Portfolio => ({
	id: p.a,
    DeskID: p.b,
    Name: p.c,
    Description: p.d,
    TakeInGlobalPosition: p.e,
    TakeInDeskPosition: p.f,
    VaRLimit: p.g,
    VolumeLimit: p.h,
    NominalLimit: p.i,
    DrawdownLimit: p.j,
    StopLossLimit: p.k,
    GammaLimit: p.l,
    VegaLimit: p.m,
    YtdPnLLimit: p.n,
	Accounts: p.A.map(mapperPortfolioAccountDTOToModel)
});

const mapperPortfolioAccountDTOToModel = (p: PortfolioAccountDTO): PortfolioAccount => ({
	id: p.a,
    PortfolioID: p.b,
    Account: p.c
});

export const mapperReportReceiverDTOToModel = (r: ReportReceiverDTO): ReportReceiver => ({
	id: r.i,
	email: r.e,
	mode: r.m
});

const mapperMonitorDeskStatusDTOToModel = (d: MonitorDeskStatusDTO): MonitorDeskStatus => ({
    DeskID: d.a,
    Desk: d.b,
    NPortfolios: d.c,
    NAccounts: d.d,
    IsValid: d.e,
    Error: d.f,
});

const mapperBaseReportSettingDTOToModel = (b: BaseReportSettingDTO): BaseReportSetting => ({
    id: b.i,
    name: b.n,
    code: b.e,
    Receivers: b.R.map(mapperReportReceiverDTOToModel)
})

export const mapperReportSettingDTOToModel = (r: ReportSettingDTO): ReportSetting => ({
    ...mapperBaseReportSettingDTOToModel(r),
    ...(r.s === true ? {
        isSpecialConfig: true,
        specialConfigAlert: r.c,
        specialConfigURL: r.u
    } : {
        isSpecialConfig: false
    })
})

const mapperGreeksReportSummaryRowDTOToModel = (d: GreeksReportSummaryRowDTO): GreeksReportSummaryRow => ({
    CommodityID: d.a,
    Commodity: d.b,
    Delta: d.c,
    Gamma: d.d,
    Vega: d.e,
    Theta: d.f
});

const mapperGreeksReportByByRowDTOToModel = (d: GreeksReportByByRowDTO): GreeksReportByByRow => ({
    id: d.a,
    PortfolioID: d.b,
    Portfolio: d.c,
    CommodityID: d.d,
    Commodity: d.e,
    GreekID: d.f,
    Greek: d.g,
    Total: d.h,
    ...mapperBaseMaturityTypeDTOToModel(d)
});

export const mapperGreeksReportDataDTOToModel = (r: GreeksReportDataDTO): GreeksReportData => ({
    reportSetting: mapperReportSettingDTOToModel(r.b),
    summaryRows: r.t.map(mapperGreeksReportSummaryRowDTOToModel),
    byDeskByProductRows: r.d.map(mapperGreeksReportByByRowDTOToModel),
    byProductByDeskRows: r.p.map(mapperGreeksReportByByRowDTOToModel),
    desksStatus: r.s.map(mapperMonitorDeskStatusDTOToModel),
    riskDesks: r.z.map(mapperRiskDeskDTOToModel)
});